import { type FC, useEffect, useState } from 'react';

import {
  Flex,
  HStack,
  Text,
  chakra,
  shouldForwardProp,
} from '@chakra-ui/react';
import { type Variants, isValidMotionProp, motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

import { LoaderAnimated } from '@/components/LoaderAnimated';
import type { CategorizedQuestionAnswerDto } from '@/services/api';

const ChakraBox = chakra(motion.div, {
  shouldForwardProp: (prop) =>
    isValidMotionProp(prop) || shouldForwardProp(prop),
});

const fadeInOutVariants: Variants = {
  hidden: {
    opacity: 0,
    transition: {
      ease: 'easeOut',
      duration: 0.3,
    },
  },
  show: {
    opacity: 1,
    transition: {
      ease: 'easeIn',
      duration: 0.3,
    },
  },
};

export const PageResultsLoading: FC<{
  skills: Array<CategorizedQuestionAnswerDto>;
}> = ({ skills }) => {
  const { t } = useTranslation(['coach']);
  const [currentSkillIndex, setCurrentSkillIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSkillIndex((prevIndex) => (prevIndex + 1) % skills.length);
    }, 2000);

    return () => clearInterval(interval);
  }, [skills.length]);

  return (
    <Flex pr={[0, 0, 0]} pl={[0, 0, 0]} flex="1" justifyContent="center">
      <Flex mt="120px" flexDirection="column" alignItems="center">
        <LoaderAnimated />
        <HStack mb={6}>
          <ChakraBox
            key={skills[currentSkillIndex]?.skill}
            initial="hidden"
            animate="show"
            exit="hidden"
            variants={fadeInOutVariants}
          >
            <Text
              textAlign="center"
              fontSize={{ base: 'lg', md: '4xl' }}
              fontWeight="bold"
            >
              {skills[currentSkillIndex]?.skill ?? ''}
            </Text>
          </ChakraBox>
          <Text fontSize={{ base: 'lg', md: '4xl' }} fontWeight="bold">
            {t('coach:result.loading.title')}
          </Text>
        </HStack>

        <Text
          textAlign="center"
          color="gray.500"
          fontSize={{ base: 'md', md: 'xl' }}
        >
          {t('coach:result.loading.desc')}
        </Text>
      </Flex>
    </Flex>
  );
};
